import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import API_CONFIG from '@/lib/config/api-config';
import { useAuth } from '@/contexts/auth';
import { tokenManager } from '@/services/tokenManager';

interface RealtimeWebSocketContextType {
  send: (data: string) => void;
  connected: boolean;
  subscribe: (cb: (msg: unknown) => void) => () => void;
}

const RealtimeWebSocketContext = createContext<RealtimeWebSocketContextType | undefined>(undefined);

const RECONNECT_DELAY_MS = 2000;

const getWebSocketBaseUrl = (): string => {
  const apiBase = API_CONFIG.BASE_URL;
  return apiBase.replace('https://', 'wss://').replace('http://', 'ws://').replace(/\/api\/?$/, '/ws/realtime/');
};

const buildWebSocketUrl = (token: string | null, customerId: string | number | null): string => {
  const baseUrl = getWebSocketBaseUrl();
  const url = new URL(baseUrl);
  if (token) url.searchParams.set('token', token);
  if (customerId != null) url.searchParams.set('customer_id', String(customerId));
  return url.toString();
};

export const RealtimeWebSocketProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const intentionalCloseRef = useRef(false);
  const [connected, setConnected] = useState(false);
  const subscribers = useRef(new Set<(msg: unknown) => void>());
  const { activeCustomer } = useAuth();

  const broadcast = useCallback((msg: unknown) => {
    subscribers.current.forEach((cb) => {
      try {
        cb(msg);
      } catch (e) {
        console.error('[WebSocket] Error in subscriber callback:', e);
      }
    });
  }, []);

  const connect = useCallback(() => {
    const token = tokenManager.getAccessToken();
    const customerId = activeCustomer?.id ?? null;
    if (!token || customerId == null) return;
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    if (wsRef.current) {
      intentionalCloseRef.current = true;
      wsRef.current.close();
      wsRef.current = null;
    }

    const url = buildWebSocketUrl(token, customerId);
    try {
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        setConnected(true);
        broadcast({ type: 'open' });
      };

      ws.onclose = () => {
        setConnected(false);
        broadcast({ type: 'close' });
        wsRef.current = null;
        if (intentionalCloseRef.current) {
          intentionalCloseRef.current = false;
          return;
        }
        const stillHasAuth = tokenManager.getAccessToken() && activeCustomer?.id != null;
        if (stillHasAuth) {
          reconnectTimeoutRef.current = setTimeout(connect, RECONNECT_DELAY_MS);
        }
      };

      ws.onerror = (e) => {
        if (!intentionalCloseRef.current) {
          console.error('[WebSocket] Error:', e);
          broadcast({ type: 'error', error: (e as ErrorEvent).message });
        }
      };

      ws.onmessage = (event) => {
        let parsed: unknown = event.data;
        try {
          if (typeof parsed === 'string') {
            parsed = JSON.parse(parsed);
          }
        } catch {
          // keep as string
        }
        broadcast(parsed);
      };
    } catch (e) {
      console.error('[WebSocket] Failed to create WebSocket:', e);
      setConnected(false);
    }
  }, [activeCustomer?.id, broadcast]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (wsRef.current) {
      intentionalCloseRef.current = true;
      wsRef.current.close();
      wsRef.current = null;
    }
    setConnected(false);
  }, []);

  // Connect when we have token and customer; reconnect when token/customer change (e.g. after refresh)
  useEffect(() => {
    const token = tokenManager.getAccessToken();
    const customerId = activeCustomer?.id ?? null;
    if (token && customerId != null) {
      connect();
    } else {
      disconnect();
    }
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
    };
  }, [activeCustomer?.id, connect, disconnect]);

  // Disconnect on explicit logout
  useEffect(() => {
    const handleAuthLogout = () => {
      disconnect();
    };
    window.addEventListener('auth-logout', handleAuthLogout);
    return () => window.removeEventListener('auth-logout', handleAuthLogout);
  }, [disconnect]);

  // Reconnect when token is updated (e.g. after refresh) so we use the new token.
  // token-refreshed is dispatched once by refreshToken() in auth.ts; storage only fires in other tabs.
  useEffect(() => {
    let reconnectScheduled = false;
    const handleTokenRefreshed = () => {
      if (reconnectScheduled) return;
      if (tokenManager.getAccessToken() && activeCustomer?.id != null) {
        reconnectScheduled = true;
        disconnect();
        setTimeout(() => {
          connect();
          reconnectScheduled = false;
        }, 0);
      }
    };
    const handleStorage = (event: StorageEvent) => {
      if (event.key === 'access_token' && event.newValue && activeCustomer?.id != null) {
        disconnect();
        setTimeout(connect, 0);
      }
    };
    window.addEventListener('token-refreshed', handleTokenRefreshed);
    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener('token-refreshed', handleTokenRefreshed);
      window.removeEventListener('storage', handleStorage);
    };
  }, [activeCustomer?.id, connect, disconnect]);

  const send = useCallback((data: string) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      return;
    }
    try {
      ws.send(data);
    } catch (e) {
      console.error('[WebSocket] Error sending message:', e);
    }
  }, []);

  const subscribe = useCallback((cb: (msg: unknown) => void) => {
    subscribers.current.add(cb);
    return () => {
      subscribers.current.delete(cb);
    };
  }, []);

  return (
    <RealtimeWebSocketContext.Provider value={{ send, connected, subscribe }}>
      {children}
    </RealtimeWebSocketContext.Provider>
  );
};

export const useRealtimeWebSocket = () => {
  const ctx = useContext(RealtimeWebSocketContext);
  if (!ctx) throw new Error('useRealtimeWebSocket must be used within a RealtimeWebSocketProvider');
  return ctx;
};
